import { useEffect } from 'react';
import Cookies from 'js-cookie';
import FacebookPixel from 'react-facebook-pixel';
import GA4 from 'react-ga4';
import {
  COOKIE_UUID_KEY,
  FB_PIXEL_ID,
  GOOGLE_MEASUREMENT_ID,
  GOOGLE_ADS_ID,
} from '../constants';
import { appsflyer, oneLinkSmartScript, tiktokPixel } from '../scripts';
import useInlineScript from './useInlineScript';
import { queryParser, detectAnalyticsSource } from '../utils';

async function sha256Hex(input) {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export const useInitAnalytics = () => {
  const queryParams = queryParser(window.location.search);
  const source = detectAnalyticsSource(queryParams);

  useInlineScript(appsflyer, 'AppsFlyerSdkObject');
  useInlineScript(oneLinkSmartScript, 'AF_SMART_SCRIPT');

  useEffect(() => {
    if (typeof window === 'undefined' || source !== 'tiktok') return;
    if (window.__TT_INIT__) return;

    const scriptId = 'TiktokJelly';
    if (document.getElementById(scriptId)) return;

    window.__TT_INIT__ = true;
    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.innerHTML = tiktokPixel;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
      window.__TT_INIT__ = false;
    };
  }, [source]);

  useEffect(() => {
    if (typeof window === 'undefined' || source !== 'google') return;
    if (window.__GA4_INITIALIZED__) return;

    const ids = [];
    if (GOOGLE_MEASUREMENT_ID) {
      ids.push({ trackingId: GOOGLE_MEASUREMENT_ID });
    }
    if (GOOGLE_ADS_ID) {
      ids.push({ trackingId: GOOGLE_ADS_ID });
    }

    if (ids.length > 0) {
      GA4.initialize(ids);
      window.__GA4_INITIALIZED__ = true;
    }
  }, [source]);

  useEffect(() => {
    if (typeof window === 'undefined' || source !== 'facebook' || !FB_PIXEL_ID)
      return;
    if (window.__FBPX_INITIALIZED__) return;

    let attempts = 0;
    const maxAttempts = 20;

    const interval = setInterval(async () => {
      attempts += 1;
      const rawId = Cookies.get(COOKIE_UUID_KEY);

      if (rawId) {
        try {
          const external_id = await sha256Hex(rawId.trim().toLowerCase());
          // @ts-expect-error - FacebookPixel
          FacebookPixel.init(FB_PIXEL_ID, { external_id });
        } catch {
          // @ts-expect-error - FacebookPixel
          FacebookPixel.init(FB_PIXEL_ID, { external_id: rawId });
        }
        FacebookPixel.pageView();
        window.__FBPX_INITIALIZED__ = true;
        clearInterval(interval);
      } else if (attempts >= maxAttempts) {
        FacebookPixel.init(FB_PIXEL_ID);
        FacebookPixel.pageView();
        window.__FBPX_INITIALIZED__ = true;
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [source]);
};
